import { useTranslation } from 'react-i18next'
import * as React from 'react'
import { Dispatch, SetStateAction } from 'react'
import { Button, Menu, MenuItemRadio, MenuList, MenuPopover, MenuProps, MenuTrigger, Tooltip } from '@fluentui/react-components'
import { SortDirection } from '../../context/GlobalContext'
import {
    ArrowSortDownLinesFilled,
    ArrowSortDownLinesRegular,
    ArrowSortUpLinesFilled,
    ArrowSortUpLinesRegular,
    bundleIcon,
} from '@fluentui/react-icons'

const SortAscIcon = bundleIcon(ArrowSortDownLinesFilled, ArrowSortDownLinesRegular)
const SortDescIcon = bundleIcon(ArrowSortUpLinesFilled, ArrowSortUpLinesRegular)

export type BaseOverviewSortMenuProps<SortingT extends BaseOverviewSortingType<any>> = {
    sortingValues?: SortingT
    setSortingValues: Dispatch<SetStateAction<SortingT>>

    headerSortingItems?: SortingT[]
}

export type BaseOverviewSortingType<SortingT> = {
    property: (keyof SortingT)[]
    direction: SortDirection
    default?: boolean
    descriptionKey?: string
}

export function BaseSortMenu<SortingT extends BaseOverviewSortingType<any>>(props: BaseOverviewSortMenuProps<SortingT>) {
    const { t: translate } = useTranslation()

    const [checkedValues, setCheckedValues] = React.useState<Record<string, string[]>>({
        sorting: props.sortingValues?.property
            ? [`${props.sortingValues?.property?.[0]?.toString()}-${props.sortingValues?.direction?.toLowerCase() || 'asc'}`]
            : [],
    })
    const onChange: MenuProps['onCheckedValueChange'] = (_e, { name, checkedItems }) => {
        setCheckedValues({
            [name]: checkedItems,
        })
    }

    return (
        <Menu positioning={{ autoSize: true }} checkedValues={checkedValues} onCheckedValueChange={onChange}>
            <MenuTrigger disableButtonEnhancement>
                <Tooltip withArrow relationship={'label'} content={translate('sorting.label')}>
                    <Button
                        icon={props.sortingValues?.direction === SortDirection.Ascending ? <SortAscIcon /> : <SortDescIcon />}
                        appearance={'secondary'}
                    />
                </Tooltip>
            </MenuTrigger>
            <MenuPopover>
                <MenuList>
                    {props.headerSortingItems?.map((item, index) => (
                        <MenuItemRadio
                            key={index}
                            name={'sorting'}
                            value={`${item.property[0].toString()}-${item.direction.toLowerCase()}`}
                            icon={item.direction === SortDirection.Ascending ? <SortAscIcon /> : <SortDescIcon />}
                            onClick={() => {
                                props.setSortingValues(item)
                            }}
                        >
                            {translate(item.descriptionKey ?? `sorting.${item.property[0].toString().toLowerCase()}${item.direction.toLowerCase()}`)}
                        </MenuItemRadio>
                    ))}
                </MenuList>
            </MenuPopover>
        </Menu>
    )
}
